import React from "react";
import styled from "styled-components";

import { BlocksContent, Button, Image } from "@global";

import {
  isAnathoth,
  isEveryday,
  LAPTOP_BREAKPOINT,
  MOBILE_BREAKPOINT,
  SMALL_LAPTOP_BREAKPOINT,
  SMALLER_MOBILE,
  TABLET_BREAKPOINT,
  TWENTY_INCH_SCREEN,
  vimeoUrls,
} from "@util/constants";
import { pageWidthMargin } from "@util/helper";
import { Container, HeroH3, StyledOverlay } from "@util/standard";
import { VideoIFrame, VideoWrapper } from "./shared.styles";

interface Props {
  sanityHero?: Queries.Maybe<Queries.SanityHero>;
  homeHero?: boolean;
  noH1?: boolean;
  classname?: string;
  // @deprecated
  height?: string;
}

const Hero = ({ homeHero, sanityHero, noH1, classname }: Props) => {
  if (!sanityHero) return null;

  const {
    heading,
    title,
    videoUrl,
    ctas,
    description,
    descriptionRich,
    textColour,
    cardColour,
    height: customHeight,
  } = sanityHero;
  const margin = pageWidthMargin();

  const getHeroHeight = (videoUrl?: string) => {
    // if (Boolean(customHeight)) return `${customHeight}vh`;
    // if (height) return height;
    if (isEveryday || isAnathoth) return "100%";
    // if (videoUrl) return "850px";
    // return "600px";
    if (Boolean(videoUrl) || homeHero) return "832px";
    return "100%";
  };

  const ContentWrap = isEveryday ? EverydayContent : isAnathoth ? AnathothContent : HeroContentContainer;

  return (
    <Wrapper
      className={isEveryday ? "everyday" : isAnathoth ? "anathoth" : undefined}
      aspect={2.76923077}
      height={getHeroHeight(videoUrl?.url ?? undefined)}
      mobileDimensions={{ height: homeHero ? "600px" : "auto" }}
      width="100%"
    >
      <Image
        isBackground
        width="100%"
        imageData={videoUrl?.image?.asset?.gatsbyImageData as any}
        mobileImage={
          videoUrl?.mobileImage?.asset?.gatsbyImageData as any
        }
      >
        {videoUrl?.url && (
          <VideoWrapper>
            <VideoIFrame
              src={vimeoUrls.background(videoUrl.url)}
              // @ts-ignore
              frameborder="0"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
            />
          </VideoWrapper>
        )}

        <ContentWrap
          variableMargin={margin}
          textColor={textColour?.value ?? "var(--theme-primary-color)"}
          backgroundColour={!isEveryday ? cardColour?.value : undefined}
        // descSize={isBarkers && homeHero ? "24px" : undefined}
        >
          {heading && <HeroH3 fontWeight="100">{heading}</HeroH3>}
          {noH1 ? <h2>{title}</h2> : <h1>{title}</h1>}
          {Boolean(descriptionRich?._rawBlocks || description) &&
            <BlocksContentContainer className={classname}>
              {descriptionRich ? (
                <BlocksContent blocks={descriptionRich?._rawBlocks} />
              ) : (
                <p>{description}</p>
              )}
            </BlocksContentContainer>}

          {ctas?.map(cta => {
            if (!cta) return null;

            return (
              <Container
                key={cta._key}
                width="80%"
                margin={isEveryday ? "0" : "24px 0px 0px"}
              >
                <Button
                  text={cta.linktext!}
                  linkTo={cta.url!}
                  theme={isEveryday || isAnathoth ? "base" : "transparent"}
                ></Button>
              </Container>
            );
          })}
        </ContentWrap>
        <StyledOverlay height="100%" opacity={isAnathoth ? 0.4 : 0.1} />
      </Image>
    </Wrapper>
  );
};

export default Hero;

const Wrapper = styled(Container)`
  overflow: hidden;
  @media only screen and (max-width: ${SMALL_LAPTOP_BREAKPOINT}px) {
    &.everyday, &.anathoth {
      min-height: 60vh;
    }
    aspect-ratio: unset;
  }

  @media only screen and (max-width: 600px) {
    &.everyday {
      p {
        width: 60%;
      }
      h1,
      h2 {
        width: 50%;
      }
    }
  }
`;

const BlocksContentContainer = styled(Container)`
  max-width: 500px;
  p {
    font-weight: 100;
    font-family: ${isAnathoth ? "var(--theme-font)" : "var(--theme-font-sub-header)"};
    font-size: 18px;
  }
  @media only screen and (max-width: 600px) {
    p {
      font-size: 16px !important;
    }
  }

  &.no-margin {
    p {
      margin: 0;
    }
  }
`;

interface ContentProps {
  variableMargin?: string;
  textColor?: string;
  backgroundColour?: Queries.Maybe<string> | undefined;
  descSize?: string;
}

const contentHeadings = `h1, h2 {
  margin: 0 0 12px 0;
  font-size: var(--theme-hero-font);
  font-family: var(--theme-font-header);
  text-transform: var(--theme-hero-transform);

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    font-size: var(--theme-hero-font-tablet);
  }
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: var(--theme-hero-font-mobile);
  }
  @media only screen and (max-width: ${SMALLER_MOBILE}px) {
    font-size: var(--theme-hero-font-small);
  }
}`;

const HeroContentContainer = styled(Container) <ContentProps>`
  height: fit-content;
  transform: translateX(0);
  padding: 40px 50px 50px 50px;
  width: 35%;
  min-width: 500px;
  z-index: 20;
  background-color: ${props =>
    props.backgroundColour
      ? props.backgroundColour
      : "var(--theme-hero-bg-color)"};

  p {
    ${({ descSize }) => `font-size: ${descSize};`}
    margin: 0;
  }

  ${contentHeadings}

  flex-direction: column;
  ${({ variableMargin }) =>
    variableMargin &&
    `
  margin: 110px auto 50px ${isAnathoth ? '100px' : '30px'};

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px){
    min-width: 100px;
  }
  @media only screen and (max-width: ${SMALL_LAPTOP_BREAKPOINT}px){
    width: calc(100% - ${variableMargin} - ${variableMargin});
    margin: 108px auto 30px auto;
    padding: 20px;
  }
  @media only screen and (min-width: ${SMALL_LAPTOP_BREAKPOINT}px) and (max-width: ${TWENTY_INCH_SCREEN}px){
    width: 45%;
  }
  `}

  ${({ textColor }) =>
    textColor &&
    `
  h1, h2, h3, p, a {
    color: ${textColor};
  }
  `};
`;

const EverydayContent = styled(Container) <ContentProps>`
  height: fit-content;
  transform: translateX(0);
  padding: 100px 50px 50px;
  max-width: 35%;
  z-index: 20;
  flex-direction: column;
  margin: auto auto auto 50px;

  @media only screen and (max-width: ${LAPTOP_BREAKPOINT}px) {
    max-width: 45%;
  }

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    min-width: 100px;
    margin: auto auto auto 30px;
    padding: 100px 30px 30px;
    max-width: 60%;
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: unset;
  }

  ${contentHeadings}
`;


const AnathothContent = styled(Container) <ContentProps>`
  transform: translateX(0);
  width: 30%;
  z-index: 20;
  flex-direction: column;
  h1, h2, p {
    color: white;
    font-weight: 400;
    text-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  }
  
  ${contentHeadings}

  ${({ variableMargin }) =>
    variableMargin &&
    `
   margin: auto auto 50px ${variableMargin};
  @media only screen and (max-width: ${LAPTOP_BREAKPOINT}px){
    margin: auto auto 30px ${variableMargin};
    width: calc(100% - ${variableMargin} - ${variableMargin});
  }
`}
`;


